import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

const SERVICES = [
  {
    n: "01",
    title: "Web Design & Engineering",
    desc: "Bespoke marketing sites and web platforms designed from a blank canvas and hand-built for speed. No themes, no page builders, no bloat.",
    items: ["Custom UI/UX design", "React / modern front-end builds", "CMS & content architecture", "Performance optimization"],
  },
  {
    n: "02",
    title: "Brand & Visual Systems",
    desc: "Logo marks, type systems, and art direction that give your business a visual identity worth remembering — applied consistently across every page.",
    items: ["Logo & identity design", "Typography systems", "Art direction", "Design guidelines"],
  },
  {
    n: "03",
    title: "Conversion Funnels",
    desc: "Landing pages and user flows engineered around a single job: turning visitors into enquiries, bookings, and sales.",
    items: ["Landing page systems", "Lead capture flows", "Booking & quote engines", "A/B testing setup"],
  },
  {
    n: "04",
    title: "Custom Backend Systems",
    desc: "APIs, dashboards, and integrations that make your site actually do something — from enquiry pipelines to client portals.",
    items: ["API development", "Admin dashboards", "Third-party integrations", "Database architecture"],
  },
];

const PACKAGES = [
  {
    n: "01",
    name: "Launch",
    price: "$500 – $1k",
    blurb: "A single-page site that punches far above its weight.",
    items: ["Custom one-page design", "Mobile-first responsive build", "Contact form & social links", "Foundational SEO setup", "Live in about 1 week"],
  },
  {
    n: "02",
    name: "Studio",
    price: "$1k – $1.5k",
    blurb: "A complete multi-page presence for a growing business.",
    items: ["Up to 5 custom pages", "Copy polish & art direction", "Quote / booking lead forms", "On-page SEO & analytics", "Live in about 2 weeks"],
  },
  {
    n: "03",
    name: "Signature",
    price: "$1.5k – $2.5k",
    blurb: "The full Fredline treatment — motion, CMS, and craft.",
    items: ["Everything in Studio", "Custom motion & scroll experiences", "CMS — edit content yourself", "Blog / project galleries", "Live in 3–4 weeks"],
    featured: true,
  },
  {
    n: "04",
    name: "Flagship",
    price: "$2,500+",
    blurb: "A bespoke digital platform with zero limits.",
    items: ["Everything in Signature", "Custom backend & integrations", "Client portals & dashboards", "Priority post-launch support", "Scoped together, in writing"],
  },
];

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-40 pb-28">
      <Seo
        title="Services — Fredline Studios"
        siteName="Fredline Studios"
        description="Web design & engineering, brand systems, conversion funnels and custom backends — the full Fredline Studios capability stack."
      />

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <span className="mono-label">CAPABILITIES</span>
          <h1 className="font-display metal-text mt-4 max-w-3xl text-5xl font-black leading-[0.95] tracking-tighter sm:text-7xl">
            THE FULL STACK OF CRAFT.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">
            Four disciplines, one team, zero hand-offs. Everything your digital presence needs under a single roof.
          </p>
        </Reveal>

        <div className="mt-20 border-t border-[#1E2335]">
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div
                data-testid={`service-row-${s.n}`}
                className="group grid gap-6 border-b border-[#1E2335] py-12 transition-colors duration-300 hover:bg-[#0D0E15] lg:grid-cols-[100px_1fr_1fr_60px] lg:items-start lg:gap-10 lg:px-6"
              >
                <span className="font-mono text-sm tracking-[0.3em] text-slate-600 transition-colors duration-300 group-hover:text-cyan-400 lg:pt-2">
                  /{s.n}
                </span>
                <div>
                  <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-100 sm:text-4xl">
                    {s.title}
                  </h2>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </div>
                <ul className="grid gap-2 lg:pt-2">
                  {s.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-slate-300">
                      <span className="h-1.5 w-1.5 rotate-45 bg-cyan-400/70" />
                      {item}
                    </li>
                  ))}
                </ul>
                <ArrowUpRight className="hidden h-6 w-6 text-slate-600 transition-all duration-300 group-hover:text-cyan-300 lg:block lg:pt-2" />
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-28">
          <span className="mono-label">PACKAGES</span>
          <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-slate-100 sm:text-5xl">
            What each budget buys.
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400">
            Straight numbers, no mystery quotes. Every package is custom-designed — the difference is scope, not care.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-px border border-[#1E2335] bg-[#1E2335] md:grid-cols-2 xl:grid-cols-4">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.07} className={p.featured ? "bg-[#12141F]" : "bg-[#0D0E15]"}>
              <div
                data-testid={`package-card-${p.name.toLowerCase()}`}
                className={`group flex h-full flex-col p-8 transition-colors duration-300 ${p.featured ? "bg-[#12141F] shadow-[inset_0_0_40px_rgba(0,240,255,0.04)]" : "hover:bg-[#12141F]"}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm tracking-[0.3em] text-slate-600 transition-colors duration-300 group-hover:text-cyan-400">/{p.n}</span>
                  {p.featured && (
                    <span className="mono-label border border-cyan-400/40 bg-cyan-400/10 px-2.5 py-1 text-[9px]">Most Forged</span>
                  )}
                </div>
                <h3 className="font-display mt-6 text-2xl font-extrabold tracking-tight text-slate-100">{p.name}</h3>
                <p className="font-display mt-2 text-xl font-bold text-cyan-300">{p.price}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.blurb}</p>
                <ul className="mt-6 flex flex-col gap-2.5 border-t border-[#1E2335] pt-6">
                  {p.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[13px] leading-snug text-slate-300">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-cyan-400/70" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`package-cta-${p.name.toLowerCase()}`}
                  className={`mt-8 inline-flex items-center justify-center px-5 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase transition-all duration-200 active:scale-[0.97] ${
                    p.featured
                      ? "bg-cyan-400 text-[#07080C] hover:bg-cyan-300 hover:shadow-[0_0_30px_rgba(0,240,255,0.4)]"
                      : "border border-[#2A3045] text-slate-200 hover:border-cyan-400/60 hover:text-cyan-300"
                  }`}
                >
                  Start with {p.name}
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-24 flex flex-col items-start justify-between gap-8 border border-[#1E2335] bg-[#0D0E15] p-10 sm:flex-row sm:items-center sm:p-14">
          <div>
            <span className="mono-label">ENGAGEMENT</span>
            <h2 className="font-display mt-3 text-2xl font-extrabold tracking-tight text-slate-100 sm:text-4xl">
              Every project starts with a conversation.
            </h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400">
              Tell us where your business is going. We'll tell you exactly what it takes to get there — scope, timeline, and price, in writing.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="services-cta-button"
            className="inline-flex shrink-0 items-center gap-2 bg-cyan-400 px-8 py-4 font-mono text-[12px] font-bold tracking-[0.2em] text-[#07080C] uppercase transition-all duration-200 hover:bg-cyan-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.45)] active:scale-[0.97]"
          >
            Get a Quote
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </main>
  );
}
